import { PaginationViewControl } from "@/Common/Components/Pagination/PaginationViewControl";
import { PortalCommunicationsView } from "@/Common/Components/SharedItem/PortalCommunicationsView";
import { CommunicationsUIStateInbox } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { usePortalCommunicationViewPagination } from "@/Communications/CustomHook/PortalCommunication/usePortalCommunicationViewPagination";

interface IPortalCommunicationViewWrapperProps {
    currentSharedItemId: string;
    communicationsUIStateInbox: CommunicationsUIStateInbox;
    onCloseCommunication: () => void;
    onPortalCommunicationsCountRefresh: () => void;
}

/**
 * Wrapper functional component for using React Hooks inside a class component context.
 *
 * According to the official React Rules of Hooks:
 * https://react.dev/warnings/invalid-hook-call-warning
 *
 * > “Hooks can only be called inside the body of a function component.”
 *
 * Since class components cannot use hooks directly, we isolate hook logic
 * inside this wrapper and render it from the class component. This approach
 * avoids breaking hook rules and keeps hook-related logic modular and testable.
 */
function PortalCommunicationViewWrapper(props: Readonly<IPortalCommunicationViewWrapperProps>): JSX.Element {
    const {
        totalSharedItems,
        absoluteIndex,
        isPreviousButtonDisabled,
        onPreviousPortalCommunication,
        onNextPortalCommunication
    } = usePortalCommunicationViewPagination(
        props.communicationsUIStateInbox,
        () => props.onCloseCommunication?.()
    );

    return (
        <PortalCommunicationsView
            currentSharedItemId={props.currentSharedItemId}
            showPatientBanner
            showCloseButton
            usedDynamicHeight
            showFlagForReview={props.communicationsUIStateInbox !== CommunicationsUIStateInbox.Sent}
            onArchivedRestore={async (sharedItemId: string) => {
                sessionStore.dispatch(
                    LocalOperations.Communications.RemovePortalCommunicationFetchedSharedItems,
                    {
                        sharedItemIds: [sharedItemId]
                    }
                );
                await onNextPortalCommunication?.(true);
            }}
            onCloseCommunication={props.onCloseCommunication}
            onPortalCommunicationsCountRefresh={props.onPortalCommunicationsCountRefresh}
            pagination={(
                <PaginationViewControl
                    itemCount={totalSharedItems}
                    isPreviousButtonDisabled={isPreviousButtonDisabled}
                    absoluteIndex={absoluteIndex}
                    onPreviousItemClick={async () => await onPreviousPortalCommunication()}
                    onNextItemClick={async () => await onNextPortalCommunication(false)}
                />
            )}
        />
    );
}

export { PortalCommunicationViewWrapper };
